import { makeAutoObservable, action, reaction, runInAction, computed, comparer } from '../../common/stores/mobx-config.js';
// FanChartManager sera importé dynamiquement pour éviter de charger D3.js au démarrage
import { debounce } from '../../utils/utils.js';
import { storeEvents, EVENTS } from '../../common/stores/storeEvents.js';
import notificationManager from '../../utils/NotificationManager.js';
import { logger } from '../../utils/debug/logger.js';

class ConfigStore {
    config = {
        fanAngle: 270,
        maxGenerations: 8,
        availableGenerations: 8,
        showMarriages: true,
        invertTextArc: true,
        coloringOption: "none",
        showMissing: true,
        dates: {
            showYearsOnly: true,
            showInvalidDates: false,
        },
        places: {
            showPlaces: true,
            showReducedPlaces: true,
        },
        givenThenFamilyName: true,
        showFirstNameOnly: true,
        substituteEvents: false,
        isTimeVisualisationEnabled: false,
        title: "",
        titleSize: 1.0,
        titleMargin: 0.25,
        weights: {
            generations: [1.0, 1.0, 1.7, 1.4],
            strokes: 0.02,
        },
        contemporary: {
            showEvents: true,
            showNames: true,
            trulyAll: false,
            generations: 1,
        },
        fanDimensions: undefined,
        frameDimensions: undefined,
        displayMode: 'screen', // 'screen' (transparent) ou 'print' (visible)
        computeChildrenCount: false,
        filename: "",
        gedcomFileName: "",
        // Stable identifier of the currently-loaded GEDCOM in files-service.
        // Set on upload (from the POST /files response) and on dropdown
        // load. Used by photosTabManager to resolve the file's primary_group
        // without relying on the (mutable) filename.
        currentFileId: null,
    };

    _batchUpdating = false;
    _queueTimeout = null;
    _updateQueued = false;
    _pendingUpdates = new Set();
    _drawInProgress = false;
    _isRootChangeInProgress = false;
    _rootChangeTimeout = null;
    _skipNextUpdate = false;
    _isInitialDraw = true;

    constructor() {
        const initialDimensions = this.calculateDimensions(
            this.config.fanAngle,
            this.config.maxGenerations,
            this.config.showMarriages
        );

        if (initialDimensions) {
            this.config.fanDimensions = initialDimensions.fanDimensionsInMm;
            this.config.frameDimensions = initialDimensions.frameDimensionsInMm;
        }

        const style = document.createElement('style');
        style.textContent = `
            .btn-outline-primary.disabled,
            .btn-outline-primary:disabled {
                background-color: var(--bg-color-dark);
                border-color: var(--color-light);
                color: var(--color-light);
                opacity: 0.5;
                cursor: not-allowed;
            }
            
            .btn-check:disabled + .btn-outline-primary, 
            .btn-check[disabled] + .btn-outline-primary {
                background-color: var(--bg-color-dark);
                border-color: var(--color-light);
                color: var(--color-light);
                opacity: 0.5;
                pointer-events: none;
            }
        `;
        document.head.appendChild(style);

        makeAutoObservable(this, {
            batchUpdate: action,
            updateFanParameter: action,
            handleSettingChange: action,
            handleSettingChangeInternal: action,
            setConfig: action,
            setGedcomFileName: action,
            queueSettingChange: action,

            angle: computed,
            dimensions: computed,

            configHistory: false,
            _rootChangeTimeout: false,
            _skipNextUpdate: false,
            _isRootChangeInProgress: false,
            _queueTimeout: false,
            _updateQueued: false,
            _batchUpdating: false,
            _pendingUpdates: false,
            _isInitialDraw: false,
        });

        // Écouter l'événement de calcul des générations maximales
        storeEvents.subscribe(EVENTS.GENERATIONS.MAX_CALCULATED, ({ maxGenerations, suggestedMax }) => {
            runInAction(() => {
                this.setConfig({ maxGenerations: suggestedMax });
                this.setAvailableGenerations(maxGenerations);
            });
        });

        // Écouteur d'événements pour le changement de root
        document.addEventListener('rootChange', () => {
            runInAction(() => {
                logger.fan.info('🔄 Détection de changement de racine');
                logger.fan.debug('Configuration des drapeaux de changement de racine');

                this._isRootChangeInProgress = true;
                this._skipNextUpdate = true;

                if (this._rootChangeTimeout) {
                    logger.fan.debug('Effacement du timeout précédent de changement de racine');
                    clearTimeout(this._rootChangeTimeout);
                }

                this._rootChangeTimeout = setTimeout(() => {
                    runInAction(() => {
                        logger.fan.debug('Réinitialisation des drapeaux de changement de racine');
                        this._isRootChangeInProgress = false;
                        this._skipNextUpdate = false;
                        this._isInitialDraw = false;
                    });
                }, 100);

                // Root change detection completed
            });
        });

        // La réaction pour les paramètres de l'éventail
        reaction(
            () => ({
                fanAngle: this.config.fanAngle,
                maxGenerations: this.config.maxGenerations,
                showMarriages: this.config.showMarriages,
                invertTextArc: this.config.invertTextArc,
                coloringOption: this.config.coloringOption,
                showMissing: this.config.showMissing
            }),
            (params, previousParams) => {
                // Ne pas traiter si un changement de root est en cours et que ce n'est pas le dessin initial
                if (this._batchUpdating || (this._isRootChangeInProgress && !this._isInitialDraw)) {
                    logger.fan.debug('⏭️ Ignorer - Mise à jour en cours ou changement de racine subséquent');
                    return;
                }

                if (previousParams && JSON.stringify(params) === JSON.stringify(previousParams)) {
                    logger.fan.debug('⏭️ Ignorer - Aucun changement réel');
                    return;
                }

                runInAction(() => {
                    // ... reste de la logique ...
                });
            },
            {
                equals: comparer.structural,
                name: 'ConfigStore-FanParametersReaction'
            }
        );

        reaction(
            () => this.config.availableGenerations,
            (availableGens) => {
                runInAction(() => {
                    const gen8Radio = document.getElementById('max-generations-8');
                    const gen8Label = document.querySelector('label[for="max-generations-8"]');
                    const gen7Radio = document.getElementById('max-generations-7');

                    if (gen8Radio && gen8Label && gen7Radio) {
                        if (availableGens < 8) {
                            // Désactiver l'option 8 générations
                            gen8Radio.disabled = true;
                            gen8Label.classList.add('disabled');

                            // Ajouter l'événement click sur le label
                            gen8Label.onclick = (e) => {
                                e.preventDefault();
                                
                                // Utiliser le NotificationManager au lieu de l'alerte bootstrap
                                notificationManager.warning(
                                    "Votre fichier Gedcom comporte moins de 8 générations"
                                );
                                
                                // Toujours passer à 7 générations
                                this.setConfig({ maxGenerations: 7 });
                                gen7Radio.checked = true;
                                gen8Radio.checked = false;
                            };

                            // Toujours passer à 7 générations
                            this.setConfig({ maxGenerations: 7 });
                            gen7Radio.checked = true;
                            gen8Radio.checked = false;
                        } else {
                            // Réactiver l'option 8 générations
                            gen8Radio.disabled = false;
                            gen8Label.classList.remove('disabled');
                            // Retirer l'événement click
                            gen8Label.onclick = null;
                        }
                    }
                });
            },
            {
                name: 'ConfigStore-GenerationButtonsReaction',
                fireImmediately: true
            }
        );
    }

    get angle() {
        return (2 * Math.PI * this.config.fanAngle) / 360.0;
    }

    get dimensions() {
        const dims = this.calculateDimensions(
            this.config.fanAngle,
            this.config.maxGenerations,
            this.config.showMarriages
        );
        
        // Si les dimensions ne sont pas définies dans la config, les calculer
        if (!this.config.fanDimensions || !this.config.frameDimensions) {
            this.config.fanDimensions = dims.fanDimensionsInMm;
            this.config.frameDimensions = dims.frameDimensionsInMm;
        }
        
        return dims;
    }

    queueSettingChange = debounce(action(() => {
        if (this._batchUpdating) return;

        logger.fan.debug('Traitement des changements de paramètres en file d\'attente');
        this._pendingUpdates.clear();
        this.handleSettingChangeInternal();
    }), 50);

    handleSettingChange = action(() => {
        logger.fan.debug('🎯 handleSettingChange appelé');
        if (this._batchUpdating) {
            logger.fan.debug('⏭️ Ignorer - mise à jour par lot en cours');
            return;
        }
        this.queueSettingChange();
    });

    handleSettingChangeInternal = action(() => {
        logger.fan.debug('🛠️ handleSettingChangeInternal');

        if (!this.config.gedcomFileName) {
            logger.fan.debug('⏭️ Ignorer la mise à jour de config: Aucun fichier GEDCOM chargé');
            return false;
        }

        // Permettre le dessin initial ou si le changement de root est terminé
        if (!this._isInitialDraw && this._isRootChangeInProgress) {
            logger.fan.debug('⏭️ Ignorer - Pas de dessin initial et changement de racine en cours');
            return false;
        }

        logger.fan.info('✨ Application des changements de configuration');
        if (this._isInitialDraw) {
            logger.fan.debug('📌 Dessin initial en cours');
        }
        
        // Charger FanChartManager dynamiquement seulement si nécessaire
        if (window.isFanChartInitialized) {
            return import('./fanChartManager.js').then(module => {
                return module.FanChartManager.applyConfigChanges();
            });
        } else {
            logger.fan.debug('⏳ FanChartManager pas encore chargé, report des changements de config');
            return Promise.resolve();
        }
    });

    batchUpdate = action((updates) => {
        if (this._batchUpdating) return;

        this._batchUpdating = true;
        try {
            updates();
            this.queueSettingChange();
        } finally {
            this._batchUpdating = false;
        }
    });

    setConfig = action((params) => {
        runInAction(() => {
            Object.assign(this.config, params);

            if (params.fanAngle !== undefined) {
                logger.fan.debug('Angle mis à jour vers:', this.angle);
            }

            // Recalculer les dimensions du frame si l'angle, les générations ou showMarriages changent
            if (params.fanAngle !== undefined || params.maxGenerations !== undefined || params.showMarriages !== undefined) {
                const newDimensions = this.calculateDimensions(
                    this.config.fanAngle,
                    this.config.maxGenerations,
                    this.config.showMarriages
                );
                if (newDimensions.fanDimensionsInMm && newDimensions.frameDimensionsInMm) {
                    this.config.fanDimensions = newDimensions.fanDimensionsInMm;
                    this.config.frameDimensions = newDimensions.frameDimensionsInMm;
                    logger.fan.debug('📐 Dimensions mises à jour:', this.config.frameDimensions);
                }
            }
        });

        if (!this._batchUpdating) {
            this.queueSettingChange();
        }
    });

    updateFanParameter = action((paramName, value) => {
        this.setConfig({ [paramName]: value });
    });

    calculateDimensions(fanAngle, maxGenerations, showMarriages) {
        const dimensionsMap = {
            270: {
                8: { fanDimensionsInMm: "301x257", frameDimensionsInMm: "331x287" },
                7: {
                    true: { fanDimensionsInMm: "301x257", frameDimensionsInMm: "331x287" },
                    false: { fanDimensionsInMm: "245x245", frameDimensionsInMm: "260x260" },
                },
            },
            360: {
                8: { fanDimensionsInMm: "297x297", frameDimensionsInMm: "331x331" },
                7: {
                    true: { fanDimensionsInMm: "297x297", frameDimensionsInMm: "331x331" },
                    false: { fanDimensionsInMm: "245x245", frameDimensionsInMm: "260x260" },
                },
            },
        };

        const defaultDimensions = { fanDimensionsInMm: undefined, frameDimensionsInMm: undefined };
        const angleDimensions = dimensionsMap[fanAngle];
        if (!angleDimensions) return defaultDimensions;

        const generationDimensions = angleDimensions[maxGenerations];
        if (!generationDimensions) return defaultDimensions;

        return generationDimensions[showMarriages] || generationDimensions;
    }

    setGedcomFileName = action((fileName) => {
        this.config.gedcomFileName = fileName;
    });

    setCurrentFileId = action((fileId) => {
        const next = fileId || null;
        if (this.config.currentFileId === next) return;
        this.config.currentFileId = next;
        // Notify subscribers — tabStateStore in particular flips tab7
        // (Photos) enabled/disabled based on whether a file is anchored
        // in files-service. Decouples configStore from tabStateStore.
        storeEvents.emit(EVENTS.FILE.CURRENT_CHANGED, { fileId: next });
    });

    get getConfig() {
        return this.config;
    }

    // Méthode pour mettre à jour le nombre de générations disponibles
    setAvailableGenerations = action((generations) => {
        this.config.availableGenerations = generations;
        logger.fan.debug(`Générations disponibles définies à: ${generations}`);
    });

    // Méthode pour réinitialiser la configuration aux valeurs par défaut
    resetToDefaults = action(() => {
        logger.fan.info('🔄 Réinitialisation du fanConfigStore aux valeurs par défaut');
        const defaultConfig = {
            fanAngle: 270,
            maxGenerations: 8,
            availableGenerations: 8,
            showMarriages: true,
            invertTextArc: true,
            coloringOption: "none",
            showMissing: true,
            dates: {
                showYearsOnly: true,
                showInvalidDates: false,
            },
            places: {
                showPlaces: true,
                showReducedPlaces: true,
            },
            givenThenFamilyName: true,
            showFirstNameOnly: true,
            substituteEvents: false,
            isTimeVisualisationEnabled: false,
            title: "",
            titleSize: 1.0,
            titleMargin: 0.25,
            weights: {
                generations: [1.0, 1.0, 1.7, 1.4],
                strokes: 0.02,
            },
            contemporary: {
                showEvents: true,
                showNames: true,
                trulyAll: false,
                generations: 1,
            },
            fanDimensions: undefined,
            frameDimensions: undefined,
            computeChildrenCount: false,
            filename: "",
            gedcomFileName: "",
        };
        
        // Préserver seulement le gedcomFileName s'il existe
        const preservedGedcomFileName = this.config.gedcomFileName;
        this.config = { ...defaultConfig };
        if (preservedGedcomFileName) {
            this.config.gedcomFileName = preservedGedcomFileName;
        }
        
        // Recalculer les dimensions avec les valeurs par défaut
        const dimensions = this.calculateDimensions(
            defaultConfig.fanAngle,
            defaultConfig.maxGenerations,
            defaultConfig.showMarriages
        );
        this.config.fanDimensions = dimensions.fanDimensionsInMm;
        this.config.frameDimensions = dimensions.frameDimensionsInMm;
    });
}

const configStore = new ConfigStore();
export default configStore;